import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../lib/api";
import { Loader, SectionHeading, EmptyState } from "../../components/Loader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Loader2, Copy } from "lucide-react";

export default function Homeowners() {
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({ queryKey: ["users", "homeowner"], queryFn: async () => (await api.get("/users?role=homeowner")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null);

  const create = async () => {
    if (!form.email || !form.name) { toast.error("Name and email required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/users", { ...form, role: "homeowner" });
      qc.invalidateQueries({ queryKey: ["users", "homeowner"] });
      setInvite(data.invite_link);
      setForm({ name: "", email: "", phone: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (u) => {
    await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
    qc.invalidateQueries({ queryKey: ["users", "homeowner"] });
  };

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-6 arq-fade-up">
      <SectionHeading overline="People" title="Homeowners">
        <Button onClick={() => { setInvite(null); setOpen(true); }} data-testid="invite-homeowner"><Plus className="h-4 w-4" />Invite Homeowner</Button>
      </SectionHeading>

      {users.length === 0 ? <EmptyState icon={Users} title="No homeowners yet" /> : (
        <div className="bg-arq-card border border-border rounded-md overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-arq-surface/60 text-left"><tr>{["Name", "Email", "Phone", "Status", ""].map((h) => <th key={h} className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.id} data-testid={`homeowner-${u.id}`} className="hover:bg-arq-surface/30">
                  <td className="px-4 py-3 font-medium text-arq-ink">{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.phone || "—"}</td>
                  <td className="px-4 py-3">{u.is_active ? <span className="text-arq-green text-xs font-medium">Active</span> : <span className="text-muted-foreground text-xs">Inactive</span>}</td>
                  <td className="px-4 py-3 text-right"><Switch checked={u.is_active} onCheckedChange={() => toggle(u)} data-testid={`toggle-${u.id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Invite Homeowner</DialogTitle></DialogHeader>
          {invite ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Homeowner created. Share this invite link so they can set their password:</p>
              <div className="flex gap-2">
                <Input value={invite} readOnly className="text-xs" data-testid="invite-link" />
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(invite); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
              </div>
              <DialogFooter><Button onClick={() => setOpen(false)}>Done</Button></DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div><Label className="mb-1.5 block">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" /></div>
                <div><Label className="mb-1.5 block">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inv-email" /></div>
                <div><Label className="mb-1.5 block">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inv-phone" /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={busy} data-testid="inv-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Create & Invite</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
